import { PartialSource } from 'app/models/options/enums';
import { createBaseUrlFromLocation, Endpoints } from 'app/api/helpers';
import { client } from 'app/api/_client';
import { getFormType, sectionFromHash } from 'app/routes/helpers';
import { getProduct, getSlug, PRODUCT } from 'app/util/headers';
import { getLoanOfficer } from 'app/reducers/loan-officer';
import { FormType, ReferralCode } from '@lenderful/domain';
import { FieldNames } from 'app/models/fields/names';
import moment from 'moment';

interface PartialRequest {
  sectionId       : number;
  path            : string;
  formVersion     : string;
  associateName?  : string;
  loanOfficerId   : number;
  formValues      : any;
  baseUrl         : string;
  source          : PartialSource;
  code?           : ReferralCode;
  slug            : string;
  product         : PRODUCT;
  formName        : string;
}

interface ReferToLoDetails {
  source: PartialSource;
  code  : ReferralCode;
}

export const getReferToLoDetails = (state): ReferToLoDetails => {
  return {
    source: state.partial.source,
    code  : state.referToLo.code,
  };
};

const SMALL_BUSINESS_FORMS: FormType[] = [FormType.SMALL_BUSINESS_LINE_OF_CREDIT, FormType.SMALL_BUSINESS_TERM];

/**
 * Small business forms collect contact details per owner instead of in the top level borrower fields
 * that the referral email reads. Uses the applicant owner (or the first owner) and falls back to the
 * business phone, as non-profits do not list owners.
 *
 * @param {*} formData
 * @returns {object} top level contact fields
 */
export const mapSmallBusinessReferralContact = (formData) => {
  const owners = formData[FieldNames.commercialOwnerList] || [];
  const owner  = owners.find((element) => element[FieldNames.ownerIsApplicant]) || owners[0] || {};
  return {
    [FieldNames.firstName] : owner[FieldNames.ownerFirstName],
    [FieldNames.lastName]  : owner[FieldNames.ownerLastName],
    [FieldNames.email]     : owner[FieldNames.ownerEmail],
    [FieldNames.phone]     : owner[FieldNames.ownerPhone] || formData[FieldNames.commercialPhone],
  };
};

export const mapPartialData = (formData, state): PartialRequest => {
  const loanOfficerId      = getLoanOfficer(state).id;
  const { hash, pathname } = state.router.location;
  const slug               = getSlug();
  const product            = getProduct();
  const baseUrl            = createBaseUrlFromLocation('resume/partial');
  const formName           = getFormType(state.router.location.pathname);

  // Removes featureToggle values from formData
  const toggles = state.config.featureToggles;
  const filteredFormData = { ...formData };
  Object.keys(toggles).forEach(toggle => {
    if (filteredFormData.hasOwnProperty(toggle)) {
      delete filteredFormData[toggle];
    }
  });

  // Only referrals, as saved partials are resumed and the top level fields would carry into the application
  const { source } = getReferToLoDetails(state);
  if (source === PartialSource.REFERRED && SMALL_BUSINESS_FORMS.includes(formName)) {
    Object.assign(filteredFormData, mapSmallBusinessReferralContact(filteredFormData));
  }

  // updates date values to only be the date

  Object.keys(filteredFormData).forEach(key => {
    if(
      moment(filteredFormData[key], moment.ISO_8601, true).isValid() &&
      filteredFormData[key]?.toString().includes('000Z')
    ) {
      filteredFormData[key] = moment(filteredFormData[key]).format( 'YYYY-MM-DD');
    }
  });

  return {
    ...getReferToLoDetails(state),
    formVersion: `${process.env.REACT_APP_VERSION || ''}`,
    path       : pathname,
    sectionId  : sectionFromHash(hash),
    formValues : filteredFormData,
    baseUrl,
    loanOfficerId,
    slug,
    product,
    formName,
  };
};

export const postPartialApp = (body) => {
  return client
    .post(Endpoints.PARTIAL_APPS, body)
    .then((response) => {
      if (response.data) {
        return response.data;
      } else {
        throw new Error('No data returned');
      }
    })
    .catch((error) => {
      throw error;
    });
};

export const putPartialApp = (body, hash, uuid) => {
  return client
    .put(`${Endpoints.PARTIAL_APPS}/${uuid}/${hash}`, body)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
};

export const getPartialApp = (body) => {
  const slug = getSlug();
  return client
    .get(`${Endpoints.PARTIAL_APPS}?uuid=${body.uuid}&hash=${body.hash}&slug=${slug}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
};
